import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import React, { Suspense, useMemo } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { buildKardaMuiTheme } from "./theme/buildKardaMuiTheme";
import { queryClient } from "./shared/api/queryClient";
import { ToastProvider } from "./shared/toast";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";

import LoginPage from "./pages/Login";
import ProfilePage from "./pages/Profile";
import HomePage from "./pages/Home";
import VideoSelectionPage from "./pages/VideoSelection";
import { TranscriptsListPage, TranscriptViewPage } from "./pages/Transcripts";
import GenerateReportPage from "./pages/GenerateReport";
import GeneratingPage from "./pages/Generating";
import EditSectionPage from "./pages/EditSection";
import ConfirmationPage from "./pages/Confirmation";
import NotFound404 from "./pages/NotFound";
import { ExtractedInfoPage } from "./pages/ExtractedInfo";
import { FinalEditsPage } from "./pages/FinalEdits";
import ReviewPlanPage from "./pages/ReviewPlan";
import { ReviewPlanRouteGuard } from "./pages/ReviewPlan/ReviewPlanRouteGuard";
import ManageUsersPage from "./pages/Admin/ManageUsers";
import { ProtectedView } from "./components/layout/ProtectedView";
import { ConnectBodycam } from "./pages/ConnectBodycam";
// Lazy: Incident Management is feature-flag-gated (and disabled outright in
// builds without an incidents API URL), so its ~50-component tree stays out
// of the main bundle for users who can never reach it.
const IncidentManagementPage = React.lazy(
  () => import("./pages/IncidentManagement")
);
const IncidentDetailPage = React.lazy(
  () => import("./pages/IncidentManagement/IncidentDetailPage")
);
import { AnalyticsPage } from "./pages/Analytics";
import { AuthTokenBridge } from "./components/auth/AuthTokenBridge";
import { AuthCallback } from "./pages/AuthCallback";
import { InitiateLogin } from "./pages/InitiateLogin";
import { RequirePermission } from "./components/auth/RequirePermission";
import { RequireCoApproval } from "./components/auth/RequireCoApproval";
import {
  USERS_ADMIN_PERMISSION,
  REPORTS_MANAGE_STATION_PERMISSION,
  EVIDENCE_EDIT_OWN_PERMISSION,
  EVIDENCE_EDIT_STATION_PERMISSION,
} from "./shared/auth/authorization";
import CoReviewQueuePage from "./pages/CoReview/CoReviewQueuePage";
import CoReviewPage from "./pages/CoReview/CoReviewPage";
import { ColorModeProvider, useColorMode } from "./context/ColorModeContext";
import { OrgIdProvider } from "./shared/auth/orgContext";
import { RequireRole } from "./components/auth/RequireRole";
import {
  KardaAdminPage,
  KardaStationDetailPage,
  StationDetailsSection,
  StationUsersSection,
  StationBodycamSection,
  StationSettingsSection,
  KardaAdminProvider,
} from "./pages/KardaAdmin";
import KardaAdminLayout from "./pages/KardaAdmin/KardaAdminLayout";
import TemplateUploadPage from "./pages/TemplateUpload/TemplateUploadPage";
import { RequireIncidentManagement } from "./components/auth/RequireIncidentManagement";

function AppContent() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildKardaMuiTheme(mode), [mode]);

  const protectedPages = (
    <>
      <Route path="/" element={<HomePage />} />
      {/* Incidents is gated per-station by the WorkOS `incident-management`
          feature flag (and requires the incidents API URL configured for this
          build) via RequireIncidentManagement. The routes stay registered so a
          hard refresh resolves to the loading spinner rather than flashing the
          catch-all 404 while auth (and its flags) restores; a genuinely
          disabled station still gets the 404, and the sidebar hides the entry. */}
      <Route
        path="/incidents"
        element={
          <RequireIncidentManagement>
            <Suspense fallback={null}>
              <IncidentManagementPage />
            </Suspense>
          </RequireIncidentManagement>
        }
      />
      <Route
        path="/incidents/:incidentId"
        element={
          <RequireIncidentManagement>
            <Suspense fallback={null}>
              <IncidentDetailPage />
            </Suspense>
          </RequireIncidentManagement>
        }
      />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/video-selection" element={<VideoSelectionPage />} />
      <Route
        path="/transcripts"
        element={
          <RequirePermission
            permission={[
              EVIDENCE_EDIT_OWN_PERMISSION,
              EVIDENCE_EDIT_STATION_PERMISSION,
            ]}
          >
            <TranscriptsListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/transcripts/:evidenceId"
        element={
          <RequirePermission
            permission={[
              EVIDENCE_EDIT_OWN_PERMISSION,
              EVIDENCE_EDIT_STATION_PERMISSION,
            ]}
          >
            <TranscriptViewPage />
          </RequirePermission>
        }
      />
      <Route
        path="/generate-report/:evidenceID"
        element={<GenerateReportPage />}
      />
      <Route path="/generating/:jobId" element={<GeneratingPage />} />
      <Route
        path="/preview-report-info/:reportID"
        element={<ExtractedInfoPage />}
      />
      <Route path="/edit-section/:reportID" element={<EditSectionPage />} />
      <Route
        path="/review-plan/:reportID?"
        element={
          <ReviewPlanRouteGuard>
            <ReviewPlanPage />
          </ReviewPlanRouteGuard>
        }
      />
      <Route path="/final-edits/:reportID" element={<FinalEditsPage />} />
      {/* Param optional: pre-rename bookmarks of the bare /confirmation sign
          page get the page's clean home redirect instead of the 404. */}
      <Route
        path="/confirmation/:reportID?"
        element={<ConfirmationPage />}
      />
      <Route
        path="/co-review"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewQueuePage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/co-review/:reportID"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewPage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/manage-users"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ManageUsersPage />
          </RequirePermission>
        }
      />
      <Route
        path="/karda-admin"
        element={
          <RequireRole roleSlug="org-karda-admin">
            <KardaAdminProvider />
          </RequireRole>
        }
      >
        <Route element={<KardaAdminLayout />}>
          <Route index element={<KardaAdminPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="templates" element={<TemplateUploadPage />} />
          <Route
            path="stations/:stationId"
            element={<KardaStationDetailPage />}
          >
            <Route index element={<StationDetailsSection />} />
            <Route path="users" element={<StationUsersSection />} />
            <Route path="bodycam" element={<StationBodycamSection />} />
            <Route path="settings" element={<StationSettingsSection />} />
          </Route>
        </Route>
      </Route>
      <Route
        path="/connect-bodycam"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ConnectBodycam />
          </RequirePermission>
        }
      />
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <Router>
        <AuthTokenBridge />
        <Routes>
          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <LoginPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/initiate-login"
            element={
              <RouteErrorBoundary>
                <InitiateLogin />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/callback"
            element={
              <RouteErrorBoundary>
                <AuthCallback />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedView>
                <RouteErrorBoundary>
                  <Outlet />
                </RouteErrorBoundary>
              </ProtectedView>
            }
          >
            {protectedPages}
          </Route>
          <Route
            path="*"
            element={
              <RouteErrorBoundary>
                <NotFound404 />
              </RouteErrorBoundary>
            }
          />
        </Routes>
      </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary variant="bare">
      {/* Above ColorModeProvider/OrgIdProvider: the query layer has no
          theme or auth dependency, and the module-singleton client must sit
          over everything that will query. */}
      <QueryClientProvider client={queryClient}>
        <ColorModeProvider>
          <OrgIdProvider>
            <AppContent />
          </OrgIdProvider>
        </ColorModeProvider>
        {/* Compiles to a no-op in production builds. */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
